import * as React from 'react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { AvailabilityBlock } from '@/features/appointments/types'
import { formatAppointmentTime } from './AppointmentsColumns'

/**
 * Compact card for one coalesced open-availability block in a calendar day
 * column. Dashed border + "Open" badge distinguish it at a glance from a
 * booked `AppointmentsCalendarCard` (solid border). Read-only — no
 * click-to-book here; booking happens via voice/duty tools, not this view.
 */
export function OpenSlotCard({ block }: { block: AvailabilityBlock }) {
  const startTime = formatAppointmentTime(block.block_start)
  const endTime = formatAppointmentTime(block.block_end)
  const timeLabel =
    startTime && endTime
      ? `${startTime} \u2013 ${endTime}`
      : (startTime ?? null)

  return (
    <div
      role="listitem"
      className={cn(
        'rounded-lg border border-dashed p-2',
        'border-primary-ui-border bg-primary-subtle-bg'
      )}>
      <div className="flex flex-wrap items-center justify-between gap-1">
        <span className="text-xs font-medium tabular-nums whitespace-nowrap">
          {timeLabel ?? <span className="text-muted-foreground">All day</span>}
        </span>
        <Badge className="rounded-md border-transparent bg-green-50 text-green-700">
          Open
        </Badge>
      </div>
      <div className="mt-1 truncate text-sm font-medium">
        {block.provider_name}
      </div>
      <div className="truncate text-xs text-muted-foreground">
        {[block.facility_name, block.appointment_type]
          .filter(Boolean)
          .join(' \u00b7 ') || 'Availability'}
      </div>
      {block.appointment_interval_id ? (
        <div className="text-xs font-medium text-primary-low-contrast">
          {block.available_capacity ?? 0}{' '}
          {block.available_capacity === 1 ? 'appointment' : 'appointments'}{' '}
          available
        </div>
      ) : block.timeslot_count > 1 ? (
        <div className="text-xs font-medium text-primary-low-contrast">
          {block.timeslot_count} slots open
        </div>
      ) : null}
    </div>
  )
}
